import { useGetEnrollmentHYCSWelcomeData, useSubmitEnrollmentHYCSWelcomeData } from '@egw/api'
import { Button, GenericError } from '@egw/components'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useEffect, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { WEALTH_ROUTES } from '@egw/constants'
import { WelcomeStep } from '../../../components'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { ROUTES } from '../../../constants/routes/routes-generic'

const log = new Logger('Enrollment - HYCS - Welcome')

export const WelcomePage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const { data, isLoading, error } = useGetEnrollmentHYCSWelcomeData()
  const { mutate, isLoading: isSubmitting } = useSubmitEnrollmentHYCSWelcomeData()

  const firstName = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.firstName : ''
  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''

  useEffect(() => {
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_WELCOME_STARTED)
  }, [])

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = async (): Promise<void> => {
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_WELCOME_CONTINUE_CLICKED, {
      applicationId,
    })
    try {
      const response = await mutate({
        proceedWelcome: true,
      })

      // If path exists and is different
      if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_WELCOME_SUBMIT, { applicationId })
        navigate(response.path)
      } else {
        // If no path from BE, redirect to error
        if (!response?.path) {
          navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
        }
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  return (
    <EnrollmentHYCSLayout title={t('hycs.welcome.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && firstName ? (
          <>
            <div>
              <div className="flex flex-col sm:flex-row mb-md sm:gap-xs gap-2xs">
                <h1 className="typo-primary-4xl-light text-text-header">{t('hycs.welcome.greeting')}</h1>
                <h1 className="typo-primary-4xl-semibold text-text-header">{firstName},</h1>
              </div>
              <p className="mb-xl typo-primary-base text-text-meta">{t('hycs.welcome.just_starting')}</p>
              <div className="flex flex-col ">
                <h3 className="typo-primary-xl-light text-text-header mb-md">{t('hycs.welcome.whats_next')}</h3>
                <WelcomeStep
                  number={1}
                  testId="fund-checking"
                  title={t('hycs.welcome.fund_checking')}
                  description={t('hycs.welcome.fund_checking_description')}
                />
                <WelcomeStep
                  number={2}
                  testId="earn-with-savings"
                  title={t('hycs.welcome.earn_with_savings')}
                  description={t('hycs.welcome.earn_with_savings_description')}
                />
                <WelcomeStep
                  number={3}
                  testId="optimize-investments"
                  title={t('hycs.welcome.optimize_investments')}
                  description={t('hycs.welcome.optimize_investments_description')}
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
              testId="primaryAction_button"
              onClick={() => {
                void onSubmit()
              }}
            >
              {t('common.continue')}
            </Button>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
